
import React, { useState, useContext, useEffect } from 'react';
import ChartBase, { useChartBase, humanizeNumber } from './chart-base';
import TweenedNumber from 'core/ui/tweened-number';
import SessionContext from 'SessionContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import LabelWithTooltip from './label-with-tooltip';

// format date into YYYYMMDD for backend.
export function formatDate(date) {
  var d = new Date(date),
      month = '' + (d.getMonth() + 1),
      day = '' + d.getDate(),
      year = d.getFullYear();
  if (month.length < 2) month = '0' + month;
  if (day.length < 2) day = '0' + day;
  return [year, month, day].join('');
}

function DateRangeWidget(props) {
  const context = useContext(SessionContext);
  const base = useChartBase(props);
  const [modal, setModal] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [startDateMax, setStartDateMax] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    setStartDate(getStartDate());
    setEndDate(getStartDate());
    setStartDateMax(getStartDate());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);
    
  // Today's date (overridable via getStartDateFn prop)
  function getStartDate(formatted) {
    if (props.getStartDateFn) return props.getStartDateFn(formatted);
    const startDate = new Date();
    return formatted ? formatDate(startDate) : startDate;
  }

  const classNames = 'count-widget geoboundary-chart';

  // Reset modal state on open/close
  const toggle = () => {
    setModal(!modal);
    setStartDate(getStartDate());
    setEndDate(getStartDate());
    setStartDateMax(getStartDate());
  };

  function getValues() {
    const config = props.config;
    const filters = props.filters || [];
    const value_series = config.ds + '.' + config.id;
    const _values = props.values[value_series] || {};
    const values = filters.map((filter) => [filter.friendly_name || filter.value, getDateRangeValueSum(filter, _values) || 0]);
    values.sort((a, b) => b[1] - a[1]);
    return values;
  }
  
  // Loop through and sum individual date counts for each filter date range
  function getDateRangeValueSum(filter, allValues) {
    const start = filter.value.split('-')[0];
    const end = filter.value.split('-')[1];
    let sum = 0;
    for (const [key, value] of Object.entries(allValues)) {
      if (key >= start && key <= end) {
        sum += value;
      }
    }
    return sum;
  }

  function onDateChange(date, ev, type) {
    ev.preventDefault && ev.preventDefault();
    switch(type) {
      case 'start':
        setStartDate(date);
        break;
      case 'end':
        setEndDate(date);
        setStartDateMax(date);
        break;
      default:
        break;
    }
  }

  function onDateCommit() {
    if (!startDate || !endDate) { return;  }
    const sDate = startDate;
    const eDate = endDate;
    const chart = props.config;
    let newStartDate = formatDate(sDate);
    let newEndDate = formatDate(eDate);
    let dateRange = newStartDate + '-' + newEndDate;
    let series = chart.ds + '.' + chart.id;
    context.crossfilterStore.add_filter(chart.id, series, dateRange, false);
    setStartDate(getStartDate());
    setEndDate(getStartDate());
    setStartDateMax(getStartDate());
    toggle();
    props.onChartUpdate && props.onChartUpdate();
  }

  // format YYYYMMDD into MM/DD/YYYY for display in widget.
  function formatDisplayDate(date) {
    if (!date) return;
    const year = date.substring(0, 4);
    const month = date.substring(4, 6);
    const day = date.substring(6, date.length);

    return month + '/' + day + '/' + year;
  }
  
  function formatDisplayDateRange(date) {
    if (!date) return;
    const dates = date.split('-');
    const startDate = formatDisplayDate(dates[0]);
    let endDate = formatDisplayDate(dates[1]) || startDate;
    return startDate + ' - ' + endDate;
  }

  function onRemoveDate(ev){
    ev.preventDefault();
    const chart = props.config;
    const entries = getValues().filter((value, index, arr) => {
      return value[0] !== ev.currentTarget.id;
    });
   
    const new_filters = entries.map((entry)=> ({series: chart.id, value: entry[0], isNegated: false}));
    context.crossfilterStore.set_filters(chart.id, new_filters);
    props.onChartUpdate && props.onChartUpdate();
  }

  base.renderSummary = () => {
    const values = getValues();
    return <ul> {values.map(([value])=> <li key={value}>{formatDisplayDateRange(value)}</li> )} </ul>;
  }

  // Pass internal state/handlers to custom renderChart override
  const internalState = {
    startDate, endDate, startDateMax, modal,
    toggle, onDateChange, onDateCommit, onRemoveDate,
    formatDisplayDateRange, getStartDate,
    chartValues: getValues()
  };
  const chartRenderer = props.renderChart
    ? () => props.renderChart({ ...props, base, ...internalState })
    : () => renderChart({...props, base});

  // Main chart content
  function renderChart({base, ...props}) {
    const chartType = props.config.label;
    const shouldTween = props.tween;
    const values = getValues();
    const viewOnly = props.viewOnly;
    const minDate = new Date('January 1, 1925');
    const maxDate = getStartDate();
    const maxDateEnd = new Date('January 1, 3000');
    return (
      <>
      {!viewOnly && <><Button size="sm" className="btn-light" onClick={toggle}>+ Add Date Range</Button>
    
      <Modal isOpen={modal} toggle={toggle} className="modal-dialog-centered date-picker">
          <ModalHeader toggle={toggle}>{chartType}</ModalHeader>
          <ModalBody>
            <div className='datepicker'>
              <div className='dp-start'>
                <label>Start Date</label>
                <DatePicker
                  startDate={startDate}
                  selected={startDate || maxDate}
                  onChange={(ev, startDate) => onDateChange(ev, startDate, 'start')}
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  minDate={minDate}
                  maxDate={startDateMax || maxDate}
                  >
                </DatePicker>
              </div>
              <div className='dp-end'>
                <label>End Date</label>
                <DatePicker
                  endDate={endDate}
                  selected={endDate}
                  onChange={(ev, endDate) => onDateChange(ev, endDate, 'end')}
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  minDate={startDate || minDate}
                  maxDate={maxDateEnd}
                >
                </DatePicker>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" size="sm" onClick={toggle}>Cancel</Button>
            <Button color="primary" className="promote" size="sm" onClick={onDateCommit}>Add Date Range</Button>{' '}
          </ModalFooter>
        </Modal>
        </>
      }
      { values.length === 0 && <p>No Filters Selected</p>}
      { values.map(([value, count]) =>
        <div className='value' key={value}>
          <LabelWithTooltip label={formatDisplayDateRange(value)} parentProps={props} />
          <i className="spacer"></i>
          {shouldTween && <TweenedNumber className='count' value={count}/>}
          {!shouldTween && <span className='count'>{humanizeNumber(count)}</span>}
          {!viewOnly && <Button color="link" size="sm" className="remove" id={value} onClick={(ev) => onRemoveDate(ev)}><i className="bi bi-trash"></i></Button>}
        </div>)}
      </>
    );
  }

  // Render using ChartBaseF
  return (
    <ChartBase {...props} base={base} chartClassName={classNames}>
      <div className={classNames}>
        {chartRenderer()}
      </div>
    </ChartBase>
  );
}

export default DateRangeWidget;
